import React, { useEffect, useState } from "react";
import "miew/dist/Miew.min.css";
import { StandaloneStructServiceProvider } from "ketcher-standalone";
import { Editor } from "ketcher-react";
import { Ketcher } from "ketcher-core";
import "ketcher-react/dist/index.css";
import Miew from "miew";
import {
  Button,
  Container,
  Form,
  Col,
  InputGroup,
  Card,
  Nav,
  Spinner,
} from "react-bootstrap";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "bootstrap/dist/css/bootstrap.min.css";
import { CompoundDataStruct } from "./sample-data";
import "./KetcherExample.css";
import FileUpload from "./Fileupload";
import PDFViewer from "./PDFViewer";
import type { Annotation } from "../utils/types";
import CompoundDisplay from "./CompoundDisplay";
import { PatentDetailsModal } from "./PatentDetailsModal";

(window as any).Miew = Miew;

const structServiceProvider = new StandaloneStructServiceProvider();

const KetcherExample: React.FC = () => {
  const [ketcher, setKetcher] = useState<Ketcher | null>(null);
  const [smiles, setSmiles] = useState<string>("");
  const [inputSmiles, setInputSmiles] = useState<string>("");
  const [uploadStatus, setUploadStatus] = useState<string | null>(null);
  const [imageURL, setImageURL] = useState<string | null>(null);
  const [compoundData, setCompoundData] = useState<CompoundDataStruct | null>(
    null
  );
  const [loading, setLoading] = useState(false);
  const [smileLoading, setSmileLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("");
  const [annotations, setAnnotations] = useState<Annotation[]>([]);

  const [showModal, setShowModal] = useState(false);
  const [patentModal, setPatentModal] = useState<any>();

  const handleShow = () => setShowModal(true);
  const handleClose = () => setShowModal(false);

  // Read the tab from URL on component mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const tabParam = urlParams.get("tab") || "draw";

    // Set the active tab based on URL parameter, defaulting to "draw"
    if (tabParam && ["draw", "upload", "upload-pdf"].includes(tabParam)) {
      setActiveTab(tabParam);
    }
  }, []);

  const handleAnnotationCreate = (annotation: Annotation) => {
    setAnnotations((prev) => [...prev, annotation]);
  };

  const handleAnnotationReset = () => {
    setAnnotations([]);
  };

  const handleAnnotationDelete = (id: string) => {
    setAnnotations((prev) => prev.filter((a) => a.id !== id));
  };

  const handleTabSelect = (tabKey: string | null) => {
    if (tabKey) {
      setActiveTab(tabKey);

      // Update URL when tab changes
      const url = new URL(window.location.href);
      url.searchParams.set("tab", tabKey);
      window.history.pushState({}, "", url.toString());
    }
  };

  const handleOnInit = async (ketcherInstance: Ketcher) => {
    try {
      setKetcher(ketcherInstance);
      (window as any).ketcher = ketcherInstance;

      const initialData = "";
      ketcherInstance.setMolecule(initialData);

      const initialSmiles = await ketcherInstance.getSmiles();
      setSmiles(initialSmiles);
    } catch (error) {
      console.log("Error:", error);
      toast.error("API request failed!");
    }
  };

  const handleSearch = async () => {
    if (!smiles) return;

    try {
      const response = await smileSearch(smiles);

      if (response && response.ok) {
        const data = await response.json();
        setImageURL(data.filePath); // Save the saved image path
        setCompoundData(data);
      } else {
        console.error("Failed to save image");
        toast.error("API request failed. Please try again.");
      }
    } catch (error) {
      console.error("Error generating image:", error);
      toast.error("Something went wrong. Try again.");
    }
  };

  useEffect(() => {
    if (!ketcher) return;

    const handleChange = async () => {
      const updatedSmiles = await ketcher.getSmiles();
      setSmiles(updatedSmiles);
      setInputSmiles(updatedSmiles);
    };

    ketcher.editor.subscribe("change", handleChange);

    return () => {
      ketcher.editor.unsubscribe("change", handleChange);
    };
  }, [ketcher]);

  const clearData = () => {
    if (ketcher && smiles) {
      ketcher.setMolecule("");
      setSmiles("");
      setInputSmiles("");
    }
    setUploadStatus("");
    setCompoundData(null);
  };

  const handleLoadSmiles = () => {
    if (inputSmiles.trim() !== "") {
      ketcher?.setMolecule(inputSmiles);
    }
  };

  const smileSearch = async (smiles: string) => {
    setSmileLoading(true);
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 2 * 1000 * 60);
    try {
      const response = await fetch(
        "https://backend.endpoints.focal-cooler-451919-j5.cloud.goog/api/search-smile",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ smileString: smiles }),
          signal: controller.signal,
        }
      );

      setSmileLoading(false);

      return response;
    } catch (error: any) {
      setSmileLoading(false);
      if (error.name === "AbortError") {
        console.error("Fetch request timed out");
      } else {
        console.error("Fetch error:", error);
      }
    } finally {
      setSmileLoading(false);
      clearTimeout(timeout);
    }
  };

  const searchCompound = async (data: FormData) => {
    setLoading(true);
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 2 * 1000 * 60);
    try {
      const response = await fetch(
        "https://backend.endpoints.focal-cooler-451919-j5.cloud.goog/api/process-image",
        {
          method: "POST",
          body: data,
          signal: controller.signal,
        }
      );
      setLoading(false);

      return response;
    } catch (error: any) {
      setLoading(false);
      if (error.name === "AbortError") {
        console.error("Fetch request timed out");
      } else {
        console.error("Fetch error:", error);
      }
    } finally {
      setLoading(false);
      clearTimeout(timeout);
    }
  };

  const handleUpload = async (selectedFile: File) => {
    if (!selectedFile) {
      setUploadStatus("Please select an image first.");
      return;
    }

    const formData = new FormData();
    formData.append("image", selectedFile);

    try {
      const response = await searchCompound(formData);
      if (response && response.ok) {
        const data = await response.json();
        setCompoundData(data);
      } else {
        setUploadStatus("Image upload failed. Please try again.");
      }
    } catch (error) {
      console.error("Error uploading image:", error);
      setUploadStatus(`Something went wrong!. Please try again. ${error}`);
    }
  };

  return (
    <main className="main-content">
      <Container>
        <Card.Body className="card-content">
          <div className="tabs">
            <Nav
              variant="tabs"
              className="tabs-list custom-tabs"
              activeKey={activeTab}
              onSelect={handleTabSelect}
            >
              <Nav.Item>
                <Nav.Link eventKey="draw" className="tab-trigger">
                  Draw Compound
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="upload" className="tab-trigger">
                  Upload Image
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="upload-pdf" className="tab-trigger">
                  Upload PDF
                </Nav.Link>
              </Nav.Item>
            </Nav>
          </div>
          <div className="tab-content-wrapper">
            {activeTab === "draw" && (
              <div className="tab-content active">
                <div style={{ height: "500px", margin: "2px" }}>
                  <Editor
                    staticResourcesUrl=""
                    structServiceProvider={structServiceProvider}
                    onInit={handleOnInit}
                    errorHandler={(message: string) => {
                      console.log("Error:", message);
                    }}
                  />
                </div>
                <Container className="mt-3">
                  {smiles && (
                    <div className="mt-3">
                      <h3>SMILES Notation:</h3>
                      <p>{smiles}</p>
                    </div>
                  )}
                  <InputGroup className="shadow-sm w-50">
                    <Form.Control
                      type="text"
                      value={inputSmiles}
                      onChange={(e) => setInputSmiles(e.target.value)}
                      placeholder="Enter SMILES to view the image in the editor"
                      className="border-primary"
                    />
                    <Button variant="primary" onClick={handleLoadSmiles}>
                      Load SMILES
                    </Button>
                  </InputGroup>
                  <div className="d-flex mt-3">
                    <Button
                      variant="success"
                      style={{ marginRight: "5px", marginLeft: "5px" }}
                      disabled={!smiles || smileLoading}
                      onClick={handleSearch}
                    >
                      {smileLoading ? "Searching..." : "Search"}
                    </Button>
                    <Button
                      variant="danger"
                      style={{ marginRight: "5px", marginLeft: "5px" }}
                      onClick={clearData}
                      disabled={loading || smileLoading}
                    >
                      Clear Data
                    </Button>
                  </div>
                </Container>
              </div>
            )}
            {activeTab === "upload" && (
              <div className="tab-content active">
                <FileUpload upload={handleUpload} loading={loading} />
              </div>
            )}
            {activeTab === "upload-pdf" && (
              <div className="tab-content active">
                <PDFViewer
                  annotations={annotations}
                  onAnnotationCreate={handleAnnotationCreate}
                  onAnnotationDelete={handleAnnotationDelete}
                  onAnnotationReset={handleAnnotationReset}
                />
              </div>
            )}
          </div>
        </Card.Body>
        {smileLoading || loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-3 text-muted">Analyzing compound...</p>
          </div>
        ) : (
          <>
            {compoundData && (
              <>
                <Col md={11}>
                  <h2 className="text-center">Search Result</h2>
                  <CompoundDisplay
                    compoundData={compoundData}
                    setPatentModal={setPatentModal}
                    handleShow={handleShow}
                  />{" "}
                </Col>
              </>
            )}

            {patentModal && (
              <>
                <PatentDetailsModal
                  patent={patentModal}
                  show={showModal}
                  onHide={handleClose}
                />
              </>
            )}
          </>
        )}
      </Container>
    </main>
  );
};

export default KetcherExample;
